import { basePath } from "@Common/constants";
import { PublicFooter } from "@Src/components/PublicFooter";
import { PublicNav } from "@Src/components/PublicNav";
import { RetrowaveBackground } from "@Src/components/RetrowaveBackground";
import { Box, Button, Container, Paper, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

export const AboutPage = () => {
	const navigate = useNavigate();

	return (
		<Box
			sx={{
				minHeight: "100vh",
				bgcolor: "background.default",
				position: "relative",
				overflow: "hidden",
			}}
		>
			<PublicNav />

			<RetrowaveBackground />

			<Container
				maxWidth="md"
				sx={{ py: { xs: 8, md: 12 }, position: "relative", zIndex: 1 }}
			>
				<Typography
					variant="h1"
					sx={{
						fontSize: { xs: "2.5rem", md: "3.5rem" },
						fontWeight: 700,
						color: "text.primary",
						mb: 6,
						textAlign: "center",
					}}
				>
					About NoMachine
				</Typography>

				<Paper
					elevation={0}
					sx={{
						p: 4,
						mb: 4,
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 2,
					}}
				>
					<Typography
						variant="h4"
						sx={{
							fontWeight: 600,
							color: "text.primary",
							mb: 2,
						}}
					>
						Our Mission
					</Typography>
					<Typography
						variant="body1"
						sx={{
							color: "text.secondary",
							mb: 3,
							lineHeight: 1.8,
						}}
					>
						NoMachine is a company based in the EU, created to address a growing
						challenge in education and recruitment: the widespread availability
						of AI tools that can complete assessments on behalf of test-takers.
						We believe that authentic evaluation is essential for identifying
						true knowledge and skills.
					</Typography>
					<Typography
						variant="body1"
						sx={{
							color: "text.secondary",
							lineHeight: 1.8,
						}}
					>
						Our platform provides a secure environment where educators and
						recruiters can create assessments with confidence, knowing that
						responses reflect genuine understanding rather than AI-generated
						content.
					</Typography>
				</Paper>

				<Paper
					elevation={0}
					sx={{
						p: 4,
						mb: 4,
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 2,
					}}
				>
					<Typography
						variant="h4"
						sx={{
							fontWeight: 600,
							color: "text.primary",
							mb: 2,
						}}
					>
						Who We Serve
					</Typography>
					<Typography
						variant="body1"
						sx={{
							color: "text.secondary",
							mb: 3,
							lineHeight: 1.8,
						}}
					>
						We built NoMachine for two key audiences:
					</Typography>

					<Box sx={{ pl: 3 }}>
						<Typography
							variant="h6"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 1,
							}}
						>
							Technical Recruiters and Hiring Teams
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								lineHeight: 1.8,
								mb: 3,
							}}
						>
							Recruitment professionals who need to evaluate candidates'
							technical knowledge and problem-solving abilities authentically,
							ensuring hiring decisions are based on real skills.
						</Typography>

						<Typography
							variant="h6"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 1,
							}}
						>
							Educators and Academic Institutions
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								lineHeight: 1.8,
							}}
						>
							Teachers, professors, and administrators who need to conduct
							exams, quizzes, and assignments that accurately measure student
							learning without AI interference.
						</Typography>
					</Box>
				</Paper>

				<Paper
					elevation={0}
					sx={{
						p: 4,
						mb: 4,
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 2,
					}}
				>
					<Typography
						variant="h4"
						sx={{
							fontWeight: 600,
							color: "text.primary",
							mb: 2,
						}}
					>
						Our Commitment
					</Typography>
					<Typography
						variant="body1"
						sx={{
							color: "text.secondary",
							mb: 2,
							lineHeight: 1.8,
						}}
					>
						We are committed to:
					</Typography>
					<Box component="ul" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
						<li>
							<Typography variant="body1" sx={{ mb: 1 }}>
								Maintaining the highest standards of security and data privacy
							</Typography>
						</li>
						<li>
							<Typography variant="body1" sx={{ mb: 1 }}>
								Continuously improving our AI prevention mechanisms
							</Typography>
						</li>
						<li>
							<Typography variant="body1" sx={{ mb: 1 }}>
								Providing excellent customer support to help you succeed
							</Typography>
						</li>
						<li>
							<Typography variant="body1" sx={{ mb: 1 }}>
								Creating an intuitive, accessible platform for all users
							</Typography>
						</li>
					</Box>
				</Paper>

				{/* CTA */}
				<Box sx={{ textAlign: "center", mt: 8 }}>
					<Typography
						variant="h2"
						sx={{
							fontSize: { xs: "2rem", md: "2.5rem" },
							fontWeight: 600,
							color: "text.primary",
							mb: 2,
						}}
					>
						Ready to Get Started?
					</Typography>
					<Typography
						variant="body1"
						sx={{
							color: "text.secondary",
							mb: 4,
							fontSize: "1.125rem",
						}}
					>
						Join organizations that trust NoMachine for authentic assessments.
					</Typography>
					<Box
						sx={{
							display: "flex",
							gap: 2,
							justifyContent: "center",
							flexWrap: "wrap",
						}}
					>
						<Button
							variant="contained"
							size="large"
							onClick={() => navigate(basePath)}
							sx={{
								px: 4,
								py: 1.5,
								textTransform: "none",
								fontSize: "1rem",
								fontWeight: 600,
							}}
						>
							Sign In
						</Button>
						<Button
							variant="outlined"
							size="large"
							onClick={() => navigate("/contact")}
							sx={{
								px: 4,
								py: 1.5,
								textTransform: "none",
								fontSize: "1rem",
								fontWeight: 600,
							}}
						>
							Contact Us
						</Button>
					</Box>
				</Box>
			</Container>

			<PublicFooter />
		</Box>
	);
};
